import _cloneDeep from "lodash.clonedeep";
import * as Sentry from "@sentry/react"
import {
  FlowEvents,
  SET_INDEX,
  ACTIVATE,
  DEACTIVATE,
  FlowObjects,
  EventKind,
  FlowMachineContext,
  SET_CURRENT_OBJECT,
  ReorderKind,
} from "./types";
import { spawn, send, actions, interpret } from "xstate";
import { assign } from "@xstate/immer";
import { MediaMachine } from "./mediamachine";
import { uploadMachine } from "./publish";
import { current } from "immer";
import guidGenerator, {
  getCurrentDate,
  dataURLtoFile,
} from "../utils/commonFunctions";
const { pure } = actions;
import { FlowWindow } from "../../globals";
import Flow from "../../model/Flow";
import { replacer } from "../../model/Action";
import { string_id, getStringId } from "../utils/getInfo";
import { OrderShift } from "../../model/Screens";
import VideoFlowObject from "../../model/VideoFlowObject";
import { throwIfWrong } from "../../model/Assets";

declare let window: FlowWindow;

/** Helper to find MMO index by id */
const findMmoIndex = (array: FlowObjects[], id: string) =>
  array.findIndex((mmo: FlowObjects) => mmo.id == id);

export const setToLoad = assign<FlowMachineContext, FlowEvents>(
  (context: FlowMachineContext, _: FlowEvents) => {
    context.loading = context.flow.screens[context.index].mixedMediaObjects
      .filter((obj: FlowObjects) => obj.type !== "text" && obj.type !== "shape")
      .map((mmo: FlowObjects) => mmo.id);
  }
);

export const setInteracting = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "SET_INTERACTING") {
      ctx.isInteracting = event.value;
    }
  }
);

export const setParentService = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "SET_PARENT_SERVICE") {
      // console.log(`[setParentService] Setting parent service`);
      ctx.parentService = event.service;

      // Now re-initialize refs with the parent service available
      // console.log(`[setParentService] Re-initializing child machines with parentService`);

      // Capture the parent service outside the Immer proxy to avoid "proxy revoked" errors
      const parentService = ctx.parentService;

      ctx.flow.screens[ctx.index].mixedMediaObjects.forEach((mmo, idx) => {
        if (mmo.__ref) {
          // Stop existing machine
          // console.log(`[setParentService] Stopping existing machine for MMO ${mmo.id}`);
          mmo.__ref.stop();
          mmo.__ref = undefined;
        }

        // Create new machine with parentService
        // console.log(`[setParentService] Creating new machine for MMO ${mmo.id} with parentService`);
        const service = interpret(
          MediaMachine.withContext({
            mmo: mmo,
            viewSize: ctx.flow.viewSize,
            screenIndex: ctx.index,
            sendAction: undefined,
            parentSend: (event: any) => {
              // console.log(`[parentSend] Child sending event to parent:`, event.type, event);
              try {
                parentService.send(event);
              } catch (error) {
                // Ignore revoked proxy errors - happens when parent transitions before child completes
                if (error instanceof TypeError && error.message.includes('revoked')) {
                  // console.log(`[parentSend] Parent proxy revoked, ignoring event:`, event.type);
                } else {
                  throw error;
                }
              }
            }
          })
        );

        service.start();
        const ref = service as any;
        ctx.flow.screens[ctx.index].mixedMediaObjects[idx].__ref = ref;
        // console.log(`[setParentService] Started machine for MMO ${mmo.id}, state:`, ref.state?.value);
        // Activate the machine
        ref.send({ type: "ACTIVATE", id: mmo.id });
      });
    }
  }
);

/**
 * Every MMO has it's own machine for handling canvas events
 * Every MMO has the `__ref` field because a spawned machine can only be called
 * by something being managed by the parent machine (in this case within the context's flow)
 */
export const initializeRefs = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, _: FlowEvents) => {
    // Capture the parent service outside the Immer proxy to avoid "proxy revoked" errors
    const parentService = ctx.parentService;

    // Spawn child machines for the current screen's MMOs
    // console.log(`[initializeRefs] Initializing screen ${ctx.index} with ${ctx.flow.screens[ctx.index].mixedMediaObjects.length} MMOs`);
    // console.log(`[initializeRefs] Flow viewSize:`, ctx.flow.viewSize);
    // console.log(`[initializeRefs] Current screen size:`, ctx.flow.screens[ctx.index].size);
    // console.log(`[initializeRefs] parentService available:`, !!parentService);
    ctx.flow.screens[ctx.index].mixedMediaObjects.forEach((mmo, idx) => {
      // Only spawn if __ref doesn't exist or was stopped
      if (!mmo.__ref) {
        // Important: Pass the MMO directly - Immer will handle the reference correctly
        // The key is that the child machine's context will hold a reference to this MMO
        // When the child commits changes, we update the array element which triggers Immer

        // BUG FIX: spawn() with .withContext() loses state config in XState v4 inside Immer
        // Use interpret() + start() and provide parentSend callback
        const service = interpret(
          MediaMachine.withContext({
            mmo: mmo,
            viewSize: ctx.flow.viewSize,
            screenIndex: ctx.index,
            sendAction: undefined,
            parentSend: parentService ? (event: any) => {
              // console.log(`[parentSend] Child sending event to parent:`, event.type, event);
              try {
                parentService.send(event);
              } catch (error) {
                // Ignore revoked proxy errors - happens when parent transitions before child completes
                if (error instanceof TypeError && error.message.includes('revoked')) {
                  // console.log(`[parentSend] Parent proxy revoked, ignoring event:`, event.type);
                } else {
                  throw error;
                }
              }
            } : undefined
          })
        );

        // Start the service and store it as ref
        service.start();

        const ref = service as any;
        // console.log(`[initializeRefs] Spawned machine state:`, ref.state?.value);
        // console.log(`[initializeRefs] Spawned machine id:`, ref.id);
        // console.log(`[initializeRefs] Machine config:`, ref.machine?.config);
        // console.log(`[initializeRefs] Machine states:`, ref.machine?.config?.states);
        // console.log(`[initializeRefs] Active state config:`, ref.machine?.config?.states?.active);
        // console.log(`[initializeRefs] SET_POSITION config:`, ref.machine?.config?.states?.active?.on?.SET_POSITION);
        // console.log(`[initializeRefs] MMO object before assign:`, mmo);
        // console.log(`[initializeRefs] MMO in array before assign:`, ctx.flow.screens[ctx.index].mixedMediaObjects[idx]);
        // console.log(`[initializeRefs] Are they the same?`, mmo === ctx.flow.screens[ctx.index].mixedMediaObjects[idx]);
        // Assign the ref back to the MMO in the array
        ctx.flow.screens[ctx.index].mixedMediaObjects[idx].__ref = ref;
        ctx.flow.screens[ctx.index].mixedMediaObjects[idx].__ref.send({ type: "ACTIVATE", id: mmo.id });
      } else {
        // MMO already has __ref, skip
      }
    });
  }
);

/**
 * Mutates context to set the current object
 * Propogate's to current object's child machine
 */
export const setCurrentObject = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === SET_CURRENT_OBJECT) {
      // console.log(`[setCurrentObject] Activating MMO ${event.id} on screen ${ctx.index}`);
      let obj = ctx.flow.screens[ctx.index].mixedMediaObjects.find(
        (mmo) => mmo.id === event.id
      )!;
      if (obj && obj.__ref) {
        // Found object with __ref, activate it
        obj.__ref!.send({ type: ACTIVATE, id: event.id });
        ctx.currentObject = obj;
      } else if (obj && !obj.__ref) {
        // __ref was lost (Immer freeze or commit mutation) - re-spawn it
        // __ref was lost - re-spawn child machine
        const parentService = ctx.parentService;
        const idx = ctx.flow.screens[ctx.index].mixedMediaObjects.findIndex(
          (mmo) => mmo.id === event.id
        );
        const service = interpret(
          MediaMachine.withContext({
            mmo: obj,
            viewSize: ctx.flow.viewSize,
            screenIndex: ctx.index,
            sendAction: undefined,
            parentSend: parentService ? (evt: any) => {
              try {
                parentService.send(evt);
              } catch (error) {
                if (error instanceof TypeError && error.message.includes('revoked')) {
                  // Ignore revoked proxy errors
                } else {
                  throw error;
                }
              }
            } : undefined
          })
        );
        service.start();
        ctx.flow.screens[ctx.index].mixedMediaObjects[idx].__ref = service as any;
        (service as any).send({ type: ACTIVATE, id: event.id });
        ctx.currentObject = ctx.flow.screens[ctx.index].mixedMediaObjects[idx];
      } else {
        console.error(`[setCurrentObject] Object ${event.id} not found`);
      }
    } else {
      ctx.currentObject = null;
    }
  }
);

/**
 * Changes the index being focused on.
 * Clears undo,redo, and stops all child machines
 */
export const setIndex = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (
      ctx.index >= 0 &&
      ctx.flow.screens[ctx.index].mixedMediaObjects != undefined
    ) {
      ctx.flow.screens[ctx.index].mixedMediaObjects.forEach(
        (mmo: FlowObjects) => {
          mmo.__ref?.stop?.();
          mmo.__ref = undefined; // Clear ref so it gets re-spawned with correct screenIndex
        }
      );
    }
    ctx.undoStack = [];
    ctx.redoStack = [];
    ctx.currentObject = null; // Clear current object when switching screens
    if (event.type === SET_INDEX) {
      ctx.index = event.type === SET_INDEX ? event.index : ctx.index;
      // Update URL to new path-based format
      const basePath = window.location.pathname.replace(/\/screen\/\d+/, '');
      window.history.pushState(
        {},
        document.title,
        `${basePath}/screen/${event.index + 1}`
      );
    }
  }
);

/**
 * Changes title of flow
 */
export const changeTitle = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "CHANGE_TITLE"
        ? {
          undo: { type: event.type, text: ctx.flow.title },
          redo: { type: event.type, text: event.text },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    if (event.type == "CHANGE_TITLE") {
      ctx.flow.title = event.text;
    }
  }
);

/**
 * Adds an mmo. Assumes the mmo is a fully loaded FlowObjects, with media objects as `File`s
 */
export const addMmo = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "ADD_MMO"
        ? {
          undo: { type: "DELETE", id: event.mmo.id, screenIndex: ctx.index },
          redo: { type: event.type, mmo: event.mmo, screenIndex: event.screenIndex },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    let drafts = ctx.drafts;
    if (event.type === "ADD_MMO" && event.draft) {
      let existing = drafts.findIndex(
        (d) => d.obj.value.id === event.draft!.id
      );
      if (existing === -1) {
        drafts.push({
          obj: { tag: "mmo", value: event.draft! },
          screen: ctx.index,
        });
      } else {
        drafts[existing] = {
          obj: { tag: "mmo", value: event.draft! },
          screen: ctx.index,
        };
      }
      ctx.publishConfig.total += determineNumDrafted(event.mmo, event);
    }
    ctx.drafts = drafts;
    let newFlow = ctx.flow;
    if (event.type == "ADD_MMO") {
      // console.info(event);
      event.mmo.__ref = spawn(
        MediaMachine.withContext({
          mmo: event.mmo,
          viewSize: ctx.flow.viewSize,
          screenIndex: ctx.index
        })
      );
      if (ctx.index >= 0 && event && event.mmo) {
        newFlow.screens[ctx.index].mixedMediaObjects.push(event.mmo);
      }
    }
    ctx.flow = newFlow;
  }
);

/**
 * Adds a background MMO at index 0 (back of z-order).
 * Same as addMmo but uses unshift instead of push.
 */
export const addBackgroundMmo = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "ADD_BACKGROUND_MMO"
        ? {
          undo: { type: "DELETE", id: event.mmo.id, screenIndex: ctx.index },
          redo: { type: event.type, mmo: event.mmo, screenIndex: event.screenIndex },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    let drafts = ctx.drafts;
    if (event.type === "ADD_BACKGROUND_MMO" && event.draft) {
      let existing = drafts.findIndex(
        (d) => d.obj.value.id === event.draft!.id
      );
      if (existing === -1) {
        drafts.push({
          obj: { tag: "mmo", value: event.draft! },
          screen: ctx.index,
        });
      } else {
        drafts[existing] = {
          obj: { tag: "mmo", value: event.draft! },
          screen: ctx.index,
        };
      }
      ctx.publishConfig.total += determineNumDrafted(event.mmo, event);
    }
    ctx.drafts = drafts;
    let newFlow = ctx.flow;
    if (event.type == "ADD_BACKGROUND_MMO") {
      event.mmo.__ref = spawn(
        MediaMachine.withContext({
          mmo: event.mmo,
          viewSize: ctx.flow.viewSize,
          screenIndex: ctx.index
        })
      );
      if (ctx.index >= 0 && event && event.mmo) {
        // Insert at index 0 (back of z-order)
        newFlow.screens[ctx.index].mixedMediaObjects.unshift(event.mmo);
      }
    }
    ctx.flow = newFlow;
  }
);

const determineNumDrafted = (newObj: FlowObjects, event: FlowEvents) => {
  if (event.type === "ADD_MMO" || event.type === "ADD_BACKGROUND_MMO") {
    switch (newObj.type) {
      case "image": {
        return 2;
      }
      case "video": {
        return (newObj as VideoFlowObject).isyoutube === "YES" ? 2 : 3;
      }
      case "pdf": {
        return 3;
      }
      case "gallery": {
        return newObj.galleryAssets!.captionTextArray.length;
      }
      case "webview": {
        return 2;
      }
      default: {
        return 0;
      }
    }
  } else if (
    event.type === "DELEGATE" &&
    event.payload.type === "CHANGE_PREVIEW_IMG"
  ) {
    return 1;
  } else if (
    event.type === "DELEGATE" &&
    event.payload.type === "CHANGE_GALLERY_ASSET"
  ) {
    return event.payload.gallery.captionTextArray.length;
  }
  return 0;
};

export const reorderIndexChanger = pure<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "REORDER_SCREENS") {
      if (ctx.index === -1) {
        return send<FlowMachineContext, FlowEvents>({
          type: "SET_INDEX",
          index: 0,
        });
      }
      if (event.screens.length <= ctx.index) {
        return send<FlowMachineContext, FlowEvents>({
          type: "SET_INDEX",
          index: ctx.index - 1,
        });
      }
    }
    if (
      event.type === "REORDER_SCREENS" &&
      typeof event.newIndex !== "undefined"
    ) {
      return send<FlowMachineContext, FlowEvents>({
        type: "SET_INDEX",
        index: event.newIndex!,
      });
    }
  }
);

export const clearLoading = assign<FlowMachineContext, FlowEvents>(
  (context: FlowMachineContext) => {
    context.loading = [];
  }
);

export const clearDrafts = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, _: FlowEvents) => {
    //@ts-ignore stops all child machines
    ctx.drafts.forEach((draft) => draft.ref.stop());
    ctx.drafts = [];
  }
);

/**
 * Informs child machine that was a currentObject it should no longer accept canvas events
 */
export const markAllInactive = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, _: FlowEvents) => {
    const screen = ctx.flow.screens[ctx.index];
    if (screen && screen.mixedMediaObjects != undefined) {
      screen.mixedMediaObjects.forEach((mmo) =>
        mmo.__ref?.send({ type: DEACTIVATE, id: mmo.id })
      );
      ctx.currentObject = null;
    }
  }
);

/**
 * Passes an event to the corresponding child machine
 */
export const delegate = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "DELEGATE") {
      // console.log(`[delegate] Received DELEGATE for MMO ${event.id}, payload type: ${event.payload.type}`);
      if (event.payload.type === "SET_POSITION") {
        // console.log(`[delegate] SET_POSITION: x=${event.payload.x}, y=${event.payload.y}`);
      }
      let mmo = ctx.flow.screens[ctx.index].mixedMediaObjects.find(
        (mmo) => mmo.id === event.id
      );
      if (!mmo) {
        // MMO not found on current screen - likely a stale callback after screen change
        return;
      }
      if (mmo.__ref != undefined) {
        // console.log(`[delegate] Child machine state:`, mmo.__ref.state?.value);
        // console.log(`[delegate] Sending ${event.payload.type} to child machine`);
        mmo.__ref!.send(event.payload);
        // console.log(`[delegate] Child machine state after send:`, mmo.__ref.state?.value);
      } else {
        // console.error(`[delegate] MMO ${event.id} has no __ref!`);
      }

      let draftIndex = ctx.drafts.findIndex(
        (val) => val.obj.value.id === event.id
      );
      if (draftIndex === -1) {
        // console.info(`TOTAL: ${ctx.publishConfig.total} => ${determineNumDrafted(mmo, event)}`);
        ctx.publishConfig.total += determineNumDrafted(mmo, event);
      }
    }
  }
);

/**
 * Adds an undo event to the stack
 */
const MAX_UNDO_STACK_SIZE = 50;

export const addUndoRedo = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event && event.type) {
      if (event.type === "ADD_UNDO_REDO") {
        ctx.undoStack = ctx.undoStack.concat([event.actions]);
        // Limit stack size to prevent memory leaks
        if (ctx.undoStack.length > MAX_UNDO_STACK_SIZE) {
          ctx.undoStack = ctx.undoStack.slice(-MAX_UNDO_STACK_SIZE);
        }
      }
    }
  }

);

/**
 * Applies any changes in a child machine to context's flow and drafts.
 * Also sets ctx.sendAction for undo/redo support (used by AI editing).
 */
export const commit = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event && event.type && event.type === "COMMIT") {
      // console.log(`[commit] Received COMMIT for MMO ${event.id} on screen ${event.screenIndex}, shouldDraft: ${event.shouldDraft}`);

      // Capture old MMO state for undo/redo BEFORE making changes
      const screenIndex = event.screenIndex ?? ctx.index;
      const idx = findMmoIndex(ctx.flow.screens[screenIndex].mixedMediaObjects, event.id);
      if (idx >= 0) {
        const oldMMO = ctx.flow.screens[screenIndex].mixedMediaObjects[idx];
        // Deep clone old MMO for undo (without __ref)
        const oldClone = _cloneDeep({ ...oldMMO, __ref: undefined });
        const newClone = _cloneDeep({ ...event.mmo, __ref: undefined });

        ctx.sendAction = {
          undo: { type: "COMMIT", mmo: oldClone, id: event.id, shouldDraft: false, screenIndex },
          redo: { type: "COMMIT", mmo: newClone, id: event.id, shouldDraft: false, screenIndex },
          id: event.id,
          kind: EventKind.Mmo,
        };
      }
    }
    let drafts = ctx.drafts;
    if (event && event.type && event.type === "COMMIT" && event.shouldDraft) {
      // Text and shape objects don't need file uploads - their content is
      // persisted in the flow JSON that publishFlow sends to the platform.
      // Adding them to drafts causes upload machine errors since they have
      // no files and cloneDeep strips class prototype methods like eventKind().
      if (event.mmo.type === "text" || event.mmo.type === "shape") {
        // Skip adding to drafts - content is already committed to flow below
      } else {
      let idx = drafts.findIndex((draft) => draft.obj.value.id === event.id);
      // Clone without __ref for drafts, but DON'T mutate event.mmo directly
      // as it may be the same reference as the array element
      const savedRef = event.mmo.__ref;
      let mmo = _cloneDeep({ ...event.mmo, __ref: undefined });
      if (idx === -1) {
        drafts.push({ obj: { tag: "mmo", value: mmo }, screen: ctx.index });
        idx = drafts.findIndex((draft) => draft.obj.value.id === event.id);
      } else {
        drafts[idx].obj.value = mmo;
      }
      let draftObject = drafts[idx].obj;
      if (draftObject.tag === "mmo") {
        let draftItem = draftObject.value;
        if (event.mmo.type !== "text" && event.mmo.type !== "shape") {
          if (
            draftItem.originalImageAsset &&
            draftItem.originalImageAsset!.externalURL.__tag === "string" &&
            !draftItem.originalImageAsset!.externalURL.value.startsWith("http")
          ) {
            draftItem.originalImageAsset!.externalURL = {
              __tag: "file",
              value: dataURLtoFile(
                draftItem.originalImageAsset!.externalURL.value,
                `${guidGenerator()}.png`
              ),
            };
          }
          if (
            draftItem.croppedImageAsset &&
            draftItem.croppedImageAsset!.externalURL.__tag === "string" &&
            !draftItem.croppedImageAsset!.externalURL.value.startsWith("http")
          ) {
            draftItem.croppedImageAsset!.externalURL = {
              __tag: "file",
              value: dataURLtoFile(
                draftItem.croppedImageAsset!.externalURL.value,
                `${guidGenerator()}.png`
              ),
            };
          }
        }
      }
      } // close else (non-text/shape)
    }
    if (event && event.type && event.type === "COMMIT") {
      // console.log(`[commit] Committing MMO ${event.id} to screen ${event.screenIndex}`);
      const idx = findIndex(ctx.flow.screens[event.screenIndex].mixedMediaObjects, event.id);
      const oldMMO = ctx.flow.screens[event.screenIndex].mixedMediaObjects[idx];

      // Stop the old child machine - it has stale MMO data
      // When user next clicks on this element, a new child machine will be spawned
      // with the updated MMO data (see setCurrentObject)
      if (oldMMO.__ref) {
        oldMMO.__ref.stop?.();
      }

      // Clear the __ref so it gets re-spawned with fresh data
      event.mmo.__ref = undefined;

      // Assign the new MMO - Immer will track this change
      ctx.flow.screens[event.screenIndex].mixedMediaObjects[idx] = event.mmo;

      // If this MMO was the current object, update it to the new version
      // and re-spawn child machine so user can continue interacting
      if (ctx.currentObject && ctx.currentObject.id === event.id) {
        const newMMO = ctx.flow.screens[event.screenIndex].mixedMediaObjects[idx];

        // Spawn a new child machine for the updated MMO
        const parentService = ctx.parentService;
        if (parentService) {
          const service = interpret(
            MediaMachine.withContext({
              mmo: newMMO,
              viewSize: ctx.flow.viewSize,
              screenIndex: event.screenIndex,
              sendAction: undefined,
              parentSend: (evt: any) => {
                try {
                  parentService.send(evt);
                } catch (error) {
                  if (error instanceof TypeError && error.message.includes('revoked')) {
                    // Ignore revoked proxy errors
                  } else {
                    throw error;
                  }
                }
              }
            })
          );
          service.start();
          newMMO.__ref = service as any;
          (service as any).send({ type: ACTIVATE, id: event.id });
        }

        // Update currentObject to point to the new MMO
        ctx.currentObject = newMMO;
      }
    }
    ctx.drafts = drafts;
  }
);

export const reorderScreens = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "REORDER_SCREENS"
        ? {
          undo: { type: event.type, screens: ctx.flow.screens },
          redo: { type: event.type, screens: event.screens },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    if (event.type === "REORDER_SCREENS") {
      if (event.kind === ReorderKind.Delete) {
        // console.log(ctx.index, ctx.drafts);
        ctx.drafts = ctx.drafts.filter((obj) => obj.screen != ctx.index);
      }

      // When copying a screen, create drafts for any base64 images
      if (event.kind === ReorderKind.Copy && typeof event.newIndex !== 'undefined') {
        const duplicatedScreen = event.screens[event.newIndex];

        duplicatedScreen.mixedMediaObjects.forEach((mmo: FlowObjects) => {
          let needsDraft = false;
          let draftMmo = _cloneDeep(mmo);
          draftMmo.__ref = undefined;

          // Check if originalImageAsset has base64 data
          if (
            draftMmo.originalImageAsset &&
            draftMmo.originalImageAsset.externalURL.__tag === "string" &&
            draftMmo.originalImageAsset.externalURL.value.startsWith("data:image")
          ) {
            needsDraft = true;
            draftMmo.originalImageAsset.externalURL = {
              __tag: "file",
              value: dataURLtoFile(
                draftMmo.originalImageAsset.externalURL.value,
                `${guidGenerator()}.png`
              ),
            };
          }

          // Check if croppedImageAsset has base64 data
          if (
            draftMmo.croppedImageAsset &&
            draftMmo.croppedImageAsset.externalURL.__tag === "string" &&
            draftMmo.croppedImageAsset.externalURL.value.startsWith("data:image")
          ) {
            needsDraft = true;
            draftMmo.croppedImageAsset.externalURL = {
              __tag: "file",
              value: dataURLtoFile(
                draftMmo.croppedImageAsset.externalURL.value,
                `${guidGenerator()}.png`
              ),
            };
          }

          // If we need a draft, add it to ctx.drafts
          if (needsDraft) {
            ctx.drafts.push({
              obj: { tag: "mmo", value: draftMmo },
              screen: event.newIndex,
            });
          }
        });
      }

      if (event.screens.length <= ctx.index) {
        ctx.index = ctx.index - 1;
      }
    }
    let newFlow = ctx.flow;
    if (event.type == "REORDER_SCREENS") newFlow.screens = event.screens;
    ctx.flow = newFlow;
  }
);

export const overrideScreen = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let newFlow = ctx.flow;
    if (event.type == "OVERRIDE_SCREEN") {
      // Use event.screenIndex if provided, otherwise fall back to ctx.index
      const targetIndex = event.screenIndex !== undefined ? event.screenIndex : ctx.index;
      if (targetIndex >= 0 && targetIndex < newFlow.screens.length) {
        newFlow.screens[targetIndex] = event.screen;
      }
    }
    ctx.flow = newFlow;
  }
);

export const overrideFlow = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let newFlow = ctx.flow;
    if (event.type == "OVERRIDE_FLOW") {
      newFlow = event.flow;
    }
    ctx.flow = newFlow;
  }
);

export const reorderMmos = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "REORDER_MMOS"
        ? {
          undo: {
            type: event.type,
            direction:
              event.direction === OrderShift.UP
                ? OrderShift.DOWN
                : OrderShift.UP,
            id: event.id,
          },
          redo: {
            type: event.type,
            direction: event.direction,
            id: event.id,
          },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    let newFlow = ctx.flow;
    if (event.type == "REORDER_MMOS") {
      let oldIndex = ctx.flow.screens[ctx.index].mixedMediaObjects.findIndex(
        (mmo: FlowObjects) => mmo.id === event.id
      );
      let newIndex =
        event.direction === OrderShift.UP ? oldIndex + 1 : Math.max(0, oldIndex - 1);
      newFlow.screens[ctx.index].mixedMediaObjects.splice(
        newIndex,
        0,
        newFlow.screens[ctx.index].mixedMediaObjects.splice(oldIndex, 1)[0]
      );
    }
    ctx.flow = newFlow;
  }
);

export const overrideScreenPreview = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let drafts = ctx.drafts;
    if (event && event.type && event.type === "OVERRIDE_SCREEN_PREVIEW") {
      let existing = drafts.findIndex(
        (d) => d.obj.tag === "asset" && d.screen === event.screenIndex
      );
      if (event.image.externalURL.__tag === "file") {
        throwIfWrong("string", event.image.externalURL);
      } else {
        let file = dataURLtoFile(
          event.image.externalURL.value,
          `${guidGenerator()}.png`
        );
        if (ctx.flow.screens[event.screenIndex] && ctx.flow.screens[event.screenIndex].previewScreenShot) {
          ctx.flow.screens[event.screenIndex].previewScreenShot.externalURL = {
            __tag: "file",
            value: file,
          };
        }

        if (ctx.flow.screens[event.screenIndex] && ctx.flow.screens[event.screenIndex].previewScreenShot_textless && ctx.flow.screens[event.screenIndex].previewScreenShot_textless)
          ctx.flow.screens[
            event.screenIndex
          ].previewScreenShot_textless!.externalURL = {
            __tag: "file",
            value: file,
          };

        if (ctx.flow.screens[event.screenIndex] && ctx.flow.screens[event.screenIndex].__fallbackScreenShot) {
          ctx.flow.screens[event.screenIndex].__fallbackScreenShot.externalURL =
            event.image.externalURL;
        }

      }
      if (ctx.flow.screens[event.screenIndex] && ctx.flow.screens[event.screenIndex].previewScreenShot) {
        if (existing === -1) {
          drafts.push({
            obj: {
              tag: "asset",
              value: ctx.flow.screens[event.screenIndex].previewScreenShot,
            },
            screen: event.screenIndex,
          });
          ctx.publishConfig.total += 1;
        } else {
          drafts[existing] = {
            obj: {
              tag: "asset",
              value: ctx.flow.screens[event.screenIndex].previewScreenShot,
            },
            screen: event.screenIndex,
          };
        }
      }
    }
    ctx.drafts = drafts;
    let newFlow = ctx.flow;
    if (event.type == "OVERRIDE_SCREEN_PREVIEW" && ctx.flow.screens[event.screenIndex] && ctx.flow.screens[event.screenIndex].previewScreenShot)
      newFlow.screens[event.screenIndex].previewScreenShot = event.image;
    ctx.flow = newFlow;
  }
);

export const deleteObject = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "DELETE") {
      let mmo = ctx.flow.screens[ctx.index].mixedMediaObjects.find(
        (mmo: FlowObjects) => mmo.id == event.id
      )!;
      if (mmo && mmo.__ref != undefined) mmo.__ref.stop();
      ctx.sendAction = {
        undo: {
          type: "ADD_MMO",
          mmo,
          screenIndex: ctx.index
        },
        redo: { type: event.type, id: event.id, screenIndex: event.screenIndex },
        id: event.id,
        kind: EventKind.Flow,
      };
    }
    ctx.currentObject = null;
    let drafted = ctx.drafts;
    if (event.type == "DELETE") {
      let idx = drafted.findIndex((draft) => draft.obj.value.id == event.id);
      if (idx > -1) {
        drafted.splice(idx, 1);
      }
    }
    ctx.drafts = drafted;
    let newFlow = ctx.flow;
    if (event.type == "DELETE") {
      let idx = findIndex(
        newFlow.screens[ctx.index].mixedMediaObjects,
        event.id
      );
      newFlow.screens[ctx.index].mixedMediaObjects.splice(idx, 1);
    }
    ctx.flow = newFlow;
  }
);

const findIndex = (array: FlowObjects[], id: string) =>
  array.findIndex((mmo: FlowObjects) => mmo.id == id);

export const setColor = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.sendAction =
      event.type === "SET_COLOR"
        ? {
          undo: {
            type: event.type,
            color:
              ctx.flow.screens[ctx.index].FlowScreenModelBackgroundColorKey,
          },
          redo: { type: event.type, color: event.color },
          id: ctx.flow.id,
          kind: EventKind.Flow,
        }
        : undefined;
    let newFlow = ctx.flow;
    if (
      event.type == "SET_COLOR" &&
      newFlow.screens[ctx.index].FlowScreenModelBackgroundColorKey != null
    )
      newFlow.screens[ctx.index].FlowScreenModelBackgroundColorKey =
        event.color;
    ctx.flow = newFlow;
  }
);

export const undo = pure<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let undoStack = ctx.undoStack;
    if (event.type === "UNDO") {
      // See `cond` in machine that will not call side effect if undoStack is empty
      let [payload] = undoStack.slice(-1);
      switch (payload.kind) {
        case EventKind.Mmo:
          return [
            { type: "SET_CURRENT_OBJECT", id: payload.id },
            {
              type: "DELEGATE",
              payload: payload.undo,
              id: payload.id,
            },
            { type: "UNDO_CLEANUP" },
          ].map((r) => send(r));
        default:
          return [payload.undo, { type: "UNDO_CLEANUP" }].map((r) => send(r));
      }
    }
  }
);

export const undoCleanup = assign<FlowMachineContext, FlowEvents>(
  // If we undo, our redo stack is invalidated
  (ctx: FlowMachineContext) => {
    let { undoStack } = ctx;
    ctx.redoStack = [undoStack[undoStack.length - 1]];
    ctx.undoStack = ctx.undoStack.slice(0, -1);
  }
);

export const redo = pure<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let redoStack = ctx.redoStack;
    if (event.type === "REDO") {
      // See `cond` in machine that will not call side effect if undoStack is empty
      let [payload] = redoStack.slice(-1);
      switch (payload.kind) {
        case EventKind.Mmo:
          return [
            { type: "SET_CURRENT_OBJECT", id: payload.id },
            {
              type: "DELEGATE",
              payload: payload.redo,
              id: payload.id,
            },
            { type: "REDO_CLEANUP" },
          ].map((r) => send(r));
        default:
          return [payload.redo, { type: "REDO_CLEANUP" }].map((r) => send(r));
      }
    }
  }
);

export const redoCleanup = assign<FlowMachineContext, FlowEvents>(
  // If we undo, our redo stack is invalidated
  (ctx: FlowMachineContext) => {
    ctx.undoStack = [ctx.redoStack[ctx.redoStack.length - 1]];
    ctx.redoStack = ctx.redoStack.slice(0, -1);
  }
);

/**
 * Applies publishConfig to context.
 * Spawns a `publishMachine` per draft object
 */
export const uploadDrafts = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    ctx.publishConfig =
      event.type === "UPLOAD_DRAFTS"
        ? {
          asDraft: event.asDraft,
          forceToView: event.forceToView,
          viewLocation: event.viewLocation || ctx.publishConfig.viewLocation,
          count: 0,
          total: ctx.publishConfig.total,
        }
        : ctx.publishConfig;
    console.log('[uploadDrafts] About to upload', ctx.drafts.length, 'drafts:', ctx.drafts.map(d => ({
      tag: d.obj.tag,
      screen: d.screen,
      id: d.obj.tag === 'mmo' ? d.obj.value.id : d.obj.tag === 'asset' ? 'preview-screenshot' : 'unknown',
      type: d.obj.tag === 'mmo' ? d.obj.value.type : 'asset'
    })));
    ctx.drafts = ctx.drafts.map((draft) => {
      draft.ref = spawn(uploadMachine.withContext({ draft: current(draft) }));
      return draft;
    });
  }
);

/**
 * After `publishMachine` completes, the returned response is applied to flow
 */
export const updateUrls = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    let flow = ctx.flow;
    if (event.type === "UPDATE_URLS") {
      if (event.uploadResponse && event.uploadResponse.length > 0) {
        if (event.kind === EventKind.Flow) {
          flow.setPreviewScreenshots(
            event.screen,
            event.uploadResponse[0].ssl_address,
            event.uploadResponse[0].contentID
          );
        } else {
          flow.screens[event.screen].setMMOUrlNew(
            event.id,
            event.uploadResponse,
            event.indexMap
          );
        }
      }
      ctx.publishConfig.count += event.uploadResponse.length;
    }
    ctx.flow = flow;
  }
);

const getAllFlowInfo = (flow: Flow) => {
  if (window.flow_json) {
    const json = window.flow_json;
    flow.updated = getCurrentDate();
    json.flowboard.flows[0] = flow;
    json.flowboard.updated = getCurrentDate();
    return {
      flow: flow,
      metadata: window.flow_metadata,
      json,
      dfi_published:
        window.flow_metadata.flowboard.document_json_published
          .generic_file_id || 0,
      dfi_draft:
        window.flow_metadata.flowboard.document_json_draft.generic_file_id || 0,
      title: flow.title,
      string_id: window.flow_json.flowboard.string_id,
    };
  }
  return {};
};

export const publishFlow = async (ctx: FlowMachineContext) => {
  let {
    metadata,
    json,
    dfi_published,
    dfi_draft,
    title,
    string_id,
  } = getAllFlowInfo(ctx.flow);

  // First save for AI-created flows: no string_id yet, create on Treemo first
  if (!string_id) {
    try {
      // Use replacer to strip internal fields like __ref before sending to Treemo
      const cleanedJson = JSON.parse(JSON.stringify(json, replacer));
      const resp = await fetch('/ai/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_json: cleanedJson, title: title || 'AI Generated Flow' }),
      });
      const result = await resp.json();
      if (result.success && result.string_id) {
        // Patch window globals so subsequent saves use normal flow
        window.flow_json.flowboard.string_id = result.string_id;
        if (result.id) {
          window.flow_metadata.flowboard.id = result.id;
        }
        // Update metadata with full document info from Treemo
        // This is critical - without proper generic_file_ids, publish won't create the published JSON file
        if (result.document_json_draft) {
          window.flow_metadata.flowboard.document_json_draft = result.document_json_draft;
        }
        if (result.document_json_published) {
          window.flow_metadata.flowboard.document_json_published = result.document_json_published;
        }
        string_id = result.string_id;
        metadata = window.flow_metadata;
        // Update URL to the real flow edit path
        history.replaceState(null, '', `/flow/${string_id}/edit`);
        // Re-read metadata after Treemo creation - now should have real generic_file_ids
        dfi_published = metadata.flowboard.document_json_published?.generic_file_id || 0;
        dfi_draft = metadata.flowboard.document_json_draft?.generic_file_id || 0;
        console.log('[publishFlow] Updated metadata from Treemo:', { dfi_published, dfi_draft });
      } else {
        console.error('[publishFlow] Failed to create flow on Treemo:', result);
        throw new Error(result.error || 'Failed to create flow on server');
      }
    } catch (error) {
      console.error('[publishFlow] Treemo creation error:', error);
      throw error;
    }
  }

  // Ensure all screens have valid preview URLs (fix for AI flows)
  // Screens without proper previews can cause Treemo to fail generating metadata
  const PLACEHOLDER_PREVIEW_URL = 'https://media.goboard.io/include/img/logos/large_logo.png';
  if (json?.flowboard?.flows?.[0]?.screens) {
    const screens = json.flowboard.flows[0].screens;
    let firstValidPreview: string | null = null;

    // Find the first valid preview URL to use as fallback
    for (const screen of screens) {
      const url = screen.previewScreenShot?.externalURL;
      const urlValue = typeof url === 'object' && url?.__tag === 'string' ? url.value : url;
      if (urlValue && typeof urlValue === 'string' && urlValue.startsWith('http')) {
        firstValidPreview = urlValue;
        break;
      }
    }

    // Apply fallback to screens with empty/invalid previews
    for (const screen of screens) {
      const url = screen.previewScreenShot?.externalURL;
      const urlValue = typeof url === 'object' && url?.__tag === 'string' ? url.value : url;

      // Check if URL is empty, undefined, or a File (not uploaded)
      const needsFallback = !urlValue ||
        (typeof urlValue === 'string' && !urlValue.startsWith('http')) ||
        (typeof urlValue === 'object');

      if (needsFallback && screen.previewScreenShot) {
        const fallbackUrl = firstValidPreview || PLACEHOLDER_PREVIEW_URL;
        screen.previewScreenShot.externalURL = { __tag: 'string', value: fallbackUrl };
        console.log('[publishFlow] Applied fallback preview URL for screen:', screen.id);
      }

      // Also fix previewScreenShot_textless if it exists
      if (needsFallback && screen.previewScreenShot_textless) {
        const fallbackUrl = firstValidPreview || PLACEHOLDER_PREVIEW_URL;
        screen.previewScreenShot_textless.externalURL = { __tag: 'string', value: fallbackUrl };
      }
    }
  }

  if (!string_id) {
    console.error('[publishFlow] string_id is required for postFlow');
    throw new Error('string_id is required for postFlow');
  }

  json = rotationFixer(json);

  const formData = new FormData();

  formData.append("document_json", JSON.stringify(json, replacer));
  formData.append("id", metadata.flowboard.id);
  formData.append("published", `${ctx.publishConfig.asDraft ? 0 : 1}`);
  formData.append("title", title!);
  formData.append(
    "parent_doc_file_ids",
    JSON.stringify({
      published: dfi_published,
      draft: dfi_draft,
    })
  );

  return fetch(`/flow/${string_id}/postFlow`, {
    method: "POST",
    body: formData,
  }).then((response) => {
    if (!response.ok) {
      console.error(`[publishFlow] postFlow returned ${response.status}: ${response.statusText}`);
    }
    return response;
  }).catch((error) => {
    console.error("[publishFlow] postFlow network error:", error);
    throw error;
  });
};

const rotationFixer = (json: any) => {
  //fix rotation on save
  for (let key in json.flowboard.flows) {
    for (let screens in json.flowboard.flows[key].screens) {
      let mmo = json.flowboard.flows[key].screens[screens].mixedMediaObjects;
      for (let objects in mmo) {
        if (
          mmo[objects].type == "shape" &&
          mmo[objects].shapeType != "rectangle"
        ) {
          // console.log("pointsArrayString ", mmo[objects].pointsArrayString);
          if (mmo[objects].rotation >= 1) {
            let times = mmo[objects].rotation;
            let [centerX, centerY] = [0.5, 0.5];
            const asArray: [number, number][] = mmo[objects].pointsArrayString
              .split(" ")
              .map((val: any) => {
                let [z, o] = val.split(",");
                return [parseFloat(z), parseFloat(o)];
              });
            let newPoints = asArray.map(([x, y]) => {
              // console.log("times", times);
              const angle = times * 90;
              var nx =
                centerX +
                (x - centerX) * Math.cos((Math.PI * angle) / 180) -
                (y - centerY) * Math.sin((Math.PI * angle) / 180);
              var ny =
                centerY +
                (y - centerY) * Math.cos((Math.PI * angle) / 180) +
                (x - centerX) * Math.sin((Math.PI * angle) / 180);

              // console.info("new point", [nx, ny]);
              return [nx, ny];
            });
            let newPointArray = "";
            for (let index = 0; index < newPoints.length; index++) {
              const [x, y] = newPoints[index];
              newPointArray += `${x.toFixed(5)},${y.toFixed(5)}`;
              if (index < newPoints.length - 1) {
                newPointArray += " ";
              }
            }
            // console.info("new point array", newPointArray);
            mmo[objects].pointsArrayString = newPointArray;
          }
        }
      }
    }
    return json;
  }
};

export const publishFlowCompletion = (ctx: FlowMachineContext, _: any) => {
  if (
    ctx.flow.screens.length > 0 &&
    ctx.flow.screens[ctx.index].mixedMediaObjects != undefined
  ) {
    ctx.flow.screens[ctx.index].mixedMediaObjects.forEach((mmo: FlowObjects) =>
      mmo.__ref?.stop()
    );
  }
  if (!ctx.publishConfig.asDraft) {
    window.location.href = `/flow/${getStringId()}/settings`;
  }
  if (ctx.publishConfig.asDraft && ctx.publishConfig.forceToView) {
    window.location.href = ctx.publishConfig.viewLocation;
  }
};

export const textboxActive = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "MARK_ACTIVE") {
      ctx.textboxId = event.id;
    } else if (event.type === "MARK_EDITOR_ACTIVE") {
      // Guard against null currentObject (can happen during screen transitions)
      if (ctx.currentObject && ctx.currentObject.id) {
        ctx.textboxId = ctx.currentObject.id;
      }
    }
  }
);

export const clearTextboxActive = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "MARK_INACTIVE") {
      ctx.textboxId = undefined;
    }
  }
);

export const updatePublishConfig = assign<FlowMachineContext, FlowEvents>(
  (ctx: FlowMachineContext, event: FlowEvents) => {
    if (event.type === "UPLOAD_DRAFTS") {
      ctx.publishConfig.asDraft = event.asDraft;
      ctx.publishConfig.forceToView = event.forceToView;
      if (event.viewLocation)
        ctx.publishConfig.viewLocation = event.viewLocation;
    }
  }
);

export const handlePublishError = (ctx: FlowMachineContext, evt: { data: any }) => {
  Sentry.addBreadcrumb({
    category: 'error',
    message: 'media upload response',
    data: { ...evt.data },
    level: Sentry.Severity.Info,
    type: 'error',
  });
}
